import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from "@mui/icons-material";

interface FileDocument {
  id: string;
  file_name: string;
  file_url: string;
  signed_url: string | null;
  category:
    | "contract"
    | "invoice"
    | "permit"
    | "receipt"
    | "warranty"
    | "other";
  upload_date: string;
  uploaded_by: string;
}

interface DocumentCardProps {
  document: FileDocument;
  canDelete: boolean;
  onDelete: (documentId: string) => Promise<void>;
}

const categoryLabels: Record<FileDocument["category"], string> = {
  contract: "Contract",
  invoice: "Invoice",
  permit: "Permit",
  receipt: "Receipt",
  warranty: "Warranty",
  other: "Other",
};

const categoryColors: Record<FileDocument["category"], string> = {
  contract: "#3b82f6",
  invoice: "#10b981",
  permit: "#f59e0b",
  receipt: "#8b5cf6",
  warranty: "#ec4899",
  other: "#6b7280",
};

const getFileIcon = (fileName: string) => {
  const extension = fileName.split(".").pop()?.toLowerCase();

  if (extension === "pdf") {
    return <PictureAsPdfIcon sx={{ fontSize: 32, color: "#dc2626" }} />;
  }
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "")) {
    return <ImageIcon sx={{ fontSize: 32, color: "#3b82f6" }} />;
  }
  return <DescriptionIcon sx={{ fontSize: 32, color: "#6b7280" }} />;
};

const DocumentCard: React.FC<DocumentCardProps> = ({
  document: doc,
  canDelete,
  onDelete,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(doc.id);
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = () => {
    try {
      const downloadUrl = doc.signed_url;
      if (!downloadUrl) {
        throw new Error("Download URL is not available");
      }

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = doc.file_name || "download";

      if (!link.href.startsWith("http")) {
        throw new Error("Invalid download URL format");
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to download file";
      console.error("Download error:", error);
      alert(`Download failed: ${errorMessage}`);
    }
  };

  const uploadDate = new Date(doc.upload_date);
  const formattedDate = uploadDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <>
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          transition: "transform 0.2s, box-shadow 0.2s",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: 6,
          },
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flex: 1,
          }}
        >
          {/* File Icon */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 2,
              bgcolor: "grey.100",
              borderRadius: 2,
            }}
          >
            {getFileIcon(doc.file_name)}
          </Box>

          {/* File Name */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                wordBreak: "break-word",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {doc.file_name}
            </Typography>
          </Box>

          {/* Category and Date */}
          <Stack spacing={1}>
            <Chip
              label={categoryLabels[doc.category]}
              size="small"
              sx={{
                bgcolor: categoryColors[doc.category],
                color: "white",
                width: "fit-content",
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Uploaded {formattedDate}
            </Typography>
          </Stack>

          {/* Actions */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: "auto", justifyContent: "flex-end" }}
          >
            <IconButton
              size="small"
              onClick={handleDownload}
              title="Download"
              color="primary"
            >
              <DownloadIcon />
            </IconButton>
            {canDelete && (
              <IconButton
                size="small"
                onClick={() => setDeleteDialogOpen(true)}
                title="Delete"
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{doc.file_name}"? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            disabled={deleting}
            variant="contained"
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentCard;
